import {
  pgTable,
  text,
  serial,
  integer,
  timestamp,
  varchar,
  boolean,
  check,
  date,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Document categories for organizations (agency, client, prospect), scoped by owner
export const orgDocumentCategories = pgTable("org_document_categories", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  ownerType: text("owner_type").notNull(), // "agency", "client", "prospect"
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_doc_categories_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

// One-level folders for documents, scoped by owner context
export const documentFolders = pgTable("document_folders", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  ownerType: text("owner_type").notNull(), // "agency", "client", "prospect"
  agencyId: varchar("agency_id"), // agency user UUID
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  // Hierarchical folders support (self-referencing)
  parentId: integer("parent_id"), // references document_folders.id (nullable)
  // Client folders only: employees of the client can see this folder + its documents
  visibleToEmployees: boolean("visible_to_employees").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_doc_folders_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

export const documents = pgTable("documents", {
  id: serial("id").primaryKey(),
  fileName: text("file_name").notNull(),
  originalFileName: text("original_file_name").notNull(),
  fileSize: integer("file_size").notNull(), // in bytes
  mimeType: text("mime_type").notNull(),
  s3Key: text("s3_key").notNull(), // S3 object key
  s3Url: text("s3_url").notNull(), // Full S3 URL

  // Document metadata
  categoryId: integer("category_id").references(() => orgDocumentCategories.id, { onDelete: "set null" }),
  category: text("category").notNull().default("other"), // free-text fallback or display name
  description: text("description"),
  tags: text("tags").array().default([]),

  // Ownership and access
  ownerId: varchar("owner_id"), // references users.id (who uploaded)
  ownerType: text("owner_type").notNull(), // "agency", "client", "employee", "prospect"
  agencyId: varchar("agency_id"), // references users.id (for agency docs - the agency user UUID)
  clientId: integer("client_id"), // references clients.id (for client-specific docs)
  employeeId: integer("employee_id"), // references employees.id (for employee-specific docs)
  prospectId: integer("prospect_id"), // references prospects.id (for prospect-specific docs)
  folderId: integer("folder_id").references(() => documentFolders.id, { onDelete: "set null" }),

  // No DB default — every insert site (documentRoutes.ts, employeeRoutes.ts)
  // must pass an explicit value.
  sharedWithEmployer: boolean("shared_with_employer").notNull(),
  sharedWithBroker: boolean("shared_with_broker").notNull(),
  visibleToEmployee: boolean("visible_to_employee").notNull(),

  // Status and workflow
  status: text("status").notNull().default("active"), // active, archived, deleted
  approvedAt: timestamp("approved_at"),
  approvedById: varchar("approved_by_id"), // references users.id

  // Version control
  version: integer("version").default(1),
  previousVersionId: integer("previous_version_id"), // references documents.id

  // Employee visibility (client documents): the folder's visibleToEmployees
  // flag is the master gate; this narrows it per document, with an optional
  // date window ("plan-year 2026 doc, visible 01/01–12/31/2026"). Default true
  // = inherit folder visibility, matching pre-existing behavior. expiresAt
  // below stays a separate hard-expiry concept.
  visibleToEmployees: boolean("visible_to_employees").notNull().default(true),
  visibilityStartDate: date("visibility_start_date"),
  visibilityEndDate: date("visibility_end_date"),

  // Audit trail
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  expiresAt: timestamp("expires_at"), // for documents that expire
}, (t) => ({
  chkOwner: check("chk_documents_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.employeeId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.employeeId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'employee' AND ${t.employeeId} IS NOT NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.employeeId} IS NULL)`),
}));

// Document access logs for compliance
export const documentAccessLogs = pgTable("document_access_logs", {
  id: serial("id").primaryKey(),
  documentId: integer("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
  accessedById: varchar("accessed_by_id").notNull(), // references users.id
  accessedByName: text("accessed_by_name").notNull(),
  accessType: text("access_type").notNull(), // "view", "download", "delete", "update"
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  accessedAt: timestamp("accessed_at").defaultNow(),
});

// Zod schemas for validation
export const insertDocumentSchema = createInsertSchema(documents, {
  fileName: z.string().min(1, "File name is required"),
  originalFileName: z.string().min(1, "Original file name is required"),
  fileSize: z.number().positive("File size must be positive"),
  mimeType: z.string().min(1, "MIME type is required"),
  s3Key: z.string().min(1, "S3 key is required"),
  s3Url: z.string().url("Valid S3 URL is required"),
  categoryId: z.number().positive().optional().nullable(),
  category: z.string().min(1, "Category is required"),
  ownerType: z.enum(["agency", "client", "employee", "prospect"]),
}).omit({
  id: true,
  uploadedAt: true,
  updatedAt: true,
});

export const insertOrgDocumentCategorySchema = createInsertSchema(orgDocumentCategories, {
  name: z.string().min(1, "Category name is required"),
  description: z.string().optional().nullable(),
  ownerType: z.enum(["agency", "client", "prospect"]),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDocumentAccessLogSchema = createInsertSchema(documentAccessLogs, {
  documentId: z.number().positive(),
  accessedById: z.string().min(1),
  accessedByName: z.string().min(1),
  accessType: z.enum(["view", "download", "delete", "update"]),
}).omit({
  id: true,
  accessedAt: true,
});

// TypeScript types
export type OrgDocumentCategory = typeof orgDocumentCategories.$inferSelect;
export type InsertOrgDocumentCategory = typeof orgDocumentCategories.$inferInsert;
export type DocumentFolder = typeof documentFolders.$inferSelect;
export type InsertDocumentFolder = typeof documentFolders.$inferInsert;
export type Document = typeof documents.$inferSelect;
export type InsertDocument = typeof documents.$inferInsert;
export type DocumentAccessLog = typeof documentAccessLogs.$inferSelect;
export type InsertDocumentAccessLog = typeof documentAccessLogs.$inferInsert;
